import React from 'react';

import {Source} from 'constants/SourceConstants';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import ContainerSchemaSelector from 'samples/ContainerSchemaSelector';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';

import {configureMapColumnNamesModalPane} from './panes/MapColumnNamesModalPane';
import ShowErrorsModalPane from './panes/ShowErrorsModalPane';
import StorableImportSettingsModalPane from './panes/StorableImportSettingsModalPane';
import UploadSpreadsheetModalPane from './panes/UploadSpreadsheetModalPane';
import * as SpreadsheetBulkImportActions from './SpreadsheetBulkImportActions';
import {
  COLUMN_TYPE,
  IMPORT_TYPE,
  Pane,
  PaneKey,
  SPREADSHEET_BULK_IMPORT_KEY,
  SpreadsheetColumn,
  STORABLE_COLUMN_TYPES,
  TRANSFER_INPUT_COLUMN_TYPES,
  TRANSFER_MEASUREMENT_TYPES,
  TRANSFER_QUANTITY_COLUMN_TYPES,
} from './SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportModalContainer from './SpreadsheetBulkImportModalContainer';
import * as StorableColumnsUtil from './storables/StorableColumnsUtil';
import * as TransferColumnsUtil from './TransferColumnsUtil';

interface Props {
  initialRegistry?: Source | null;
  onRequestClose: () => void;
  actionOrigin: ActionOrigin;
}

export default class BulkCreateContainersFlow extends React.Component<Props> {
  static open(props: Omit<Props, 'onRequestClose'>): void {
    LegacyModalContainer.open(
      SPREADSHEET_BULK_IMPORT_KEY,
      <BulkCreateContainersFlow {...props} onRequestClose={SpreadsheetBulkImportActions.closeModal} />
    );
  }

  _getPanes = (): Array<Pane> => [
    {
      paneKey: PaneKey.SET_STORABLE_IMPORT_SETTINGS,
      configuration: StorableImportSettingsModalPane.configure({
        schemaSelectorClass: ContainerSchemaSelector,
        schemaIdAccessorKey: 'containerSchemaId',
        showProjectSelector: true,
      }),
    },
    {
      paneKey: PaneKey.UPLOAD_SPREADSHEET,
      configuration: UploadSpreadsheetModalPane.configure({
        getExampleImageURL() {
          return '/static/img/spreadsheet-import-example-create-containers.png';
        },
      }),
    },
    {
      paneKey: PaneKey.MAP_COLUMN_NAMES,
      configuration: configureMapColumnNamesModalPane({
        getValidColumnTypes() {
          return [
            ...STORABLE_COLUMN_TYPES,
            COLUMN_TYPE.NUMBER_OF_CONTAINERS,
            COLUMN_TYPE.CONTAINER_SCHEMA_FIELD,
            ...TRANSFER_INPUT_COLUMN_TYPES,
            ...TRANSFER_MEASUREMENT_TYPES,
            ...StorableColumnsUtil.getSampleControlRolesColumns(),
            COLUMN_TYPE.IGNORE,
          ];
        },
        getRequiredColumnsError(columns: Array<SpreadsheetColumn>) {
          const inputColumnsError = TransferColumnsUtil.getInputColumnsError(columns, false);
          if (inputColumnsError) {
            return inputColumnsError;
          }
          const measurementColumnsError = TransferColumnsUtil.getMeasurementColumnsError(columns, true);
          if (measurementColumnsError) {
            return measurementColumnsError;
          }
          const hasTransferVolumeColumn = columns.some(({type}) =>
            TRANSFER_QUANTITY_COLUMN_TYPES.includes(type)
          );
          const hasSourceColumn = columns.some(({type}) => TRANSFER_INPUT_COLUMN_TYPES.includes(type));
          if (hasTransferVolumeColumn && !hasSourceColumn) {
            return `Source must be specified with ${getQuantityDisplayString(false)}.`;
          }
          const requiredParentStorableError = StorableColumnsUtil.getRequiredParentStorableError(columns);
          if (requiredParentStorableError) {
            return requiredParentStorableError;
          }
          if (columns.some(({type}) => type !== COLUMN_TYPE.IGNORE)) {
            return null;
          }
          return 'At least one column must be specified.';
        },
        onValidateSpreadsheet: SpreadsheetBulkImportActions.validateSpreadsheetForCreateContainers,
      }),
    },
    {
      paneKey: PaneKey.SHOW_ERRORS,
      configuration: ShowErrorsModalPane.configure({
        enableReplaceLinkWithText: true,
        enableSkipErrors: true,
      }),
    },
  ];

  override render(): JSX.Element {
    return (
      <SpreadsheetBulkImportModalContainer
        importItemWord="container"
        importType={IMPORT_TYPE.CONTAINERS}
        initialSettings={{initialRegistry: this.props.initialRegistry}}
        onImportFromSpreadsheet={SpreadsheetBulkImportActions.createContainersFromSpreadsheet}
        analyticsMetadata={{actionOrigin: this.props.actionOrigin}}
        onRequestClose={this.props.onRequestClose}
        panes={this._getPanes()}
        title="Import containers"
      />
    );
  }
}
